import { App } from 'vue';

/**
 * Vue plugin that provides feature flag functionality to the application
 *
 * @remarks
 * This plugin initializes a feature flag service and provides it to the Vue application
 *
 * @example
 * ```ts
 * // Usage in main.ts
 * app.use(FeatureFlagPlugin, {
 *   sUrl: 'https://feature-flags-api.example.com',
 *   callback: (featureFlag) => {
 *     // Do something with the initialized feature flag service
 *   }
 * });
 * ```
 *
 * @public
 */
export const TgnFeatureFlag = {
  async install(app: App, { sUrl, callback }: FeatureFlagOptions) {
    const FEATURE_FLAG = useFeatureFlag(sUrl);

    await FEATURE_FLAG.init().then(() => {
      app.provide('FEATURE_FLAG', FEATURE_FLAG);
      callback(FEATURE_FLAG);
    });
  },
};

/**
 * A hook for managing feature flags fetched from a remote URL.
 *
 * @param url - The URL to fetch feature flags from
 * @returns An object containing methods to interact with feature flags
 * @property {function} isOn - Check if a feature flag is enabled
 * @property {function} isOff - Check if a feature flag is disabled
 * @property {function} init - Initialize the feature flags by fetching from the URL
 * @property {function} refreshFeatures - Refresh the feature flags by fetching from the URL again
 *
 * @example
 * ```
 * const { isOn, isOff, init } = useFeatureFlag('https://api.example.com/features');
 *
 * // Initialize feature flags
 * await init();
 *
 * // Check if a feature is enabled
 * if (isOn('newDashboard')) {
 *   // Show new dashboard
 * }
 * ```
 *
 * @throws Will throw an error if initialization or refresh fails
 */
function useFeatureFlag(url: URL) {
  const features: Features = {} as Features;

  const isOn = (featureName: keyof Features): boolean =>
    !!features[featureName]?.defaultValue;
  const isOff = (featureName: keyof Features): boolean =>
    !features[featureName]?.defaultValue;

  const setFeatures = (newFeatures: Features) => {
    Object.keys(newFeatures).forEach((key) => {
      features[key as keyof Features] = newFeatures[key as keyof Features];
    });
  };

  const init = async () => {
    const newFeatures = await getFeatures();
    if (newFeatures) return setFeatures(newFeatures);
    else throw new Error('Something went wrong initiating Feature Flags plugin');
  };

  const refreshFeatures = async () => {
    const refreshedFeatures = await getFeatures();
    if (refreshedFeatures) return setFeatures(refreshedFeatures);
    else throw new Error('Something went wrong refreshing Feature Flags plugin');
  };

  const getFeatures = async () => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const { features } = await response.json();

      if (!features) {
        throw new Error('No features found in the response');
      }

      return features as Features;
    } catch (error) {
      console.error('Error fetching feature flags:', error);
    }
  };

  const getByFlagName = (featureName: string) => {
    return features[featureName as keyof Features] ?? null;
  }


  return { isOn, isOff, init, refreshFeatures, getByFlagName };
}

export type URL = `https://${string}` | `http://${string}`;
export type Features = Record<'featureName', { defaultValue: boolean }>;
export type FeatureFlag = ReturnType<typeof useFeatureFlag>;
export type FeatureFlagOptions = { sUrl: URL; callback: (P: FeatureFlag) => void };
